import React, { useState } from 'react';
import { BoardStage } from '../../../entities/board';
import type { Lead, LeadsFilters } from '../../../entities/lead';
import { Select } from '../../../shared/ui/Select';
import { Button } from '../../../shared/ui/Button';
import { UserSearchInput } from '../../../shared/ui/UserSearchInput/UserSearchInput';
import { KanbanColumn } from './KanbanColumn';

interface KanbanBoardProps {
  stages: BoardStage[];
  filters?: LeadsFilters;
  isFiltered: boolean;
  canManage: boolean;
  onCardClick: (id: string, title?: string) => void;
  onMoveLead: (leadId: string, stageId: string) => Promise<void>;
  onBulkUpdate: (leadIds: string[], patch: Partial<Lead>, label: string) => Promise<void>;
  isLoading: boolean;
}

type BulkMode = 'move' | 'assign';

const KanbanBoard: React.FC<KanbanBoardProps> = ({
  stages,
  filters,
  isFiltered,
  canManage,
  onCardClick,
  onMoveLead,
  onBulkUpdate,
  isLoading,
}) => {
  const [draggingLeadId, setDraggingLeadId] = useState<string | null>(null);
  const [activeDropStageId, setActiveDropStageId] = useState<string | null>(null);
  const [mode, setMode] = useState<BulkMode | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [targetStage, setTargetStage] = useState('');
  const [targetUser, setTargetUser] = useState<{ id: string; name: string }>({ id: '', name: '' });
  const [applying, setApplying] = useState(false);

  const resetSelection = () => {
    setMode(null);
    setSelectedIds(new Set());
    setTargetStage('');
    setTargetUser({ id: '', name: '' });
  };

  const startMode = (next: BulkMode) => {
    if (mode === next) {
      resetSelection();
      return;
    }
    setMode(next);
    setSelectedIds(new Set());
  };

  const toggleSelect = (leadId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  };

  const canApply = mode === 'move' ? Boolean(targetStage) : Boolean(targetUser.id);

  const handleApply = async () => {
    if (!canApply || !selectedIds.size) return;
    const ids = Array.from(selectedIds);
    const patch = mode === 'move' ? { boardId: targetStage } : { userId: targetUser.id };
    const label = mode === 'move'
      ? `Перенесено карточек: ${ids.length}`
      : `Переназначено карточек: ${ids.length}`;
    setApplying(true);
    try {
      await onBulkUpdate(ids, patch, label);
      resetSelection();
    } finally {
      setApplying(false);
    }
  };

  if (isLoading) {
    return (
      <div className="kanban-wrap" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'var(--text2)', fontSize: 15, fontWeight: 600 }}>Загрузка...</div>
      </div>
    );
  }

  if (stages.length === 0) {
    return (
      <div className="kanban-wrap" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'var(--text2)', fontSize: 15, fontWeight: 600 }}>
          Колонки доски не найдены
        </div>
      </div>
    );
  }

  return (
    <div className="kanban-wrap">
      {canManage && (
        <div className="kanban-toolbar">
          {mode === null ? (
            <>
              <Button variant="secondary" size="sm" onClick={() => startMode('move')}>Перенести</Button>
              <Button variant="secondary" size="sm" onClick={() => startMode('assign')}>Назначить</Button>
            </>
          ) : (
            <>
              <span className="kanban-toolbar-count">Выбрано: {selectedIds.size}</span>
              <div style={{ minWidth: 220 }}>
                {mode === 'move' ? (
                  <Select
                    options={stages.map(s => ({ value: s.id, label: s.name }))}
                    value={targetStage}
                    onChange={setTargetStage}
                    placeholder="Выберите этап"
                    portal
                  />
                ) : (
                  <UserSearchInput
                    value={targetUser.id}
                    selectedName={targetUser.name}
                    onSelect={(id, name) => setTargetUser({ id, name })}
                    onClear={() => setTargetUser({ id: '', name: '' })}
                    placeholder="Выберите пользователя"
                    portal
                  />
                )}
              </div>
              <Button
                variant="primary"
                size="sm"
                disabled={!canApply || !selectedIds.size || applying}
                onClick={handleApply}
              >
                {applying ? 'Применение...' : 'Применить'}
              </Button>
              <Button variant="secondary" size="sm" onClick={resetSelection}>Отмена</Button>
            </>
          )}
        </div>
      )}
      <div className="kanban-scroll">
        {stages.map(stage => (
          <KanbanColumn
            key={stage.id}
            stage={stage}
            filters={filters}
            isFiltered={isFiltered}
            onCardClick={onCardClick}
            draggingLeadId={draggingLeadId}
            isDropTarget={activeDropStageId === stage.id}
            onDragStart={setDraggingLeadId}
            onDragEnd={() => {
              setDraggingLeadId(null);
              setActiveDropStageId(null);
            }}
            onDragOverStage={stageId => setActiveDropStageId(stageId)}
            onDragLeaveStage={stageId => {
              if (activeDropStageId === stageId) {
                setActiveDropStageId(null);
              }
            }}
            onDropLead={async (leadId, stageId) => {
              setActiveDropStageId(null);
              setDraggingLeadId(null);
              await onMoveLead(leadId, stageId);
            }}
            selectionMode={mode !== null}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
          />
        ))}
      </div>
    </div>
  );
};

export { KanbanBoard };
